import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const Testimonial = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const gender = useMemo(() => {
        return formContext.answers.gender;
    }, [formContext.answers.gender]);
    const pronouns = useMemo(() => {
        return formContext.answers.pronouns;
    }, [formContext.answers.pronouns]);
    const [quoteHeight, setQuoteHeight] = useState(null);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            rowGap: '24px'
        },
        slideLeft: {
            padding: '0 0 0 56px',
            transform: 'translate(100vw, -495px)',
            animation: '$slideLeft 0.5s cubic-bezier(0, 0, 0.2, 1) forwards 0.2s'
        },
        image: {
            zIndex: 1,
            width: '168px',
            height: '168px',
            borderRadius: '50%',
            border: `8px solid ${theme.palette.white}`,
            marginBottom: '24px'
        },
        leftQuotationMarks: {
            position: 'absolute',
            zIndex: 1,
            transform: 'translate(-56px, -15px)'
        },
        rightQuotationMarks: {
            position: 'absolute',
            zIndex: 1,
            transform: `translate(232px, ${quoteHeight}px)`,
            [theme.breakpoints.up('lg')]: {
                transform: 'translate(430px, 90px)'
            }
        },
        textSection: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '36px',
            marginBottom: '32px'
        },
        quote: {
            fontWeight: TypographyWeights.MEDIUM,
            zIndex: 1
        },
        nameSection: {
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
        },
        name: {
            fontWeight: TypographyWeights.BOLD
        },
        certification: {
            fontWeight: TypographyWeights.SEMI_BOLD
        },
        circleTakeover: {
            display: 'grid',
            gridTemplateColumns: '39px 1fr 39px',
            gridGap: '16px',
            flexDirection: 'row',
            width: '495px',
            height: '495px',
            borderRadius: '50%',
            backgroundColor: theme.palette.warning['200'],
            padding: '80px 180px',
            transform: 'translate(-200vw, 0)',
            animation: '$slideRight 0.5s forwards 0.2s'
        },
        '@keyframes slideRight': {
            '0%': {
                transform: 'translate(-200vw, 0)' 
            },
            '100%': {
                transform: 'translate(-234px, 0)' 
            },
        },
        '@keyframes slideLeft': {
            '0%': {
                transform: 'translate(100vw, -495px)' 
            },
            '100%': {
                transform: 'translate(0, -495px)' 
            },
        }
    })), [quoteHeight]);
    const classes = useStyles();
    const theme = useTheme();

    const onClick = () => {
        formContext.goNext();
    };

    const getContent = useCallback(() => {
        let imagePath = '/img/sunnyside/advisors/andrew_tatarsky.png';
        let altText = 'Headshot of Dr. Andrew Tatarsky, a Sunnyside advisor.';
        let quote = 'Sunnyside has been demonstrated to be very effective for men seeking to change their relationship with alcohol. Providing more flexible and approachable options to support the development of healthier habits around drinking is critical. Sunnyside is the perfect tool for the job.';
        let name = 'Dr. Andrew Tatarsky, PhD';
        let certification = 'Director of Clinical Programming at Freedom Institute, Alcohol Use Psychologist';

        if (gender === 'Female' && pronouns === 'She / Her') {
            imagePath = '/img/sunnyside/advisors/kate_bonham.webp';
            altText = 'Headshot of Katherine Thompson, a Sunnyside advisor.';
            quote = 'Sunnyside has been demonstrated to be especially effective for women seeking to change their relationship with alcohol. In a time when drinking is on the rise among women, it\'s more important than ever to provide them with better options for changing habits in a positive, proactive way. Sunnyside is the perfect tool for the job.';
            name = 'Katherine Thompson';
            certification = 'MS, LPCC, LADC - Mental Health and Alcohol Health Specialist';
        }

        return {
            imagePath,
            altText,
            quote,
            name,
            certification 
        };
    }, [gender, pronouns]);

    const {
        imagePath, altText, quote, name, certification 
    } = getContent();

    const quoteElement = useRef(null);

    useEffect(() => {
        if (!quoteElement.current) return;

        setQuoteHeight(quoteElement.current.clientHeight);
    }, [quoteElement]);

    return (
        <CardContainer
            sectionKey={'aboutYou'}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'aboutYou'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Box className={classes.circleTakeover} />
                        <Box className={classes.slideLeft}>
                            <img
                                className={classes.image}
                                src={imagePath}
                                alt={altText}
                                width={777}
                                height={777}
                            />
                            <Box className={classes.textSection}>
                                <svg
                                    className={classes.leftQuotationMarks}
                                    width="39"
                                    height="26"
                                    viewBox="0 0 39 26"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M28.9903 26C27.1961 26 25.6223 25.5238 24.2688 24.5714C22.9153 23.619 21.8608 22.3651 21.1053 20.8095C20.3814 19.254 20.0194 17.5556 20.0194 15.7143C20.0194 12.6032 21.0581 9.63492 23.1356 6.80952C25.2131 3.98413 27.9201 1.71429 31.2567 0L39 4.38095C36.9225 5.36508 35.0969 6.53968 33.523 7.90476C31.9492 9.26984 30.8475 10.8571 30.2179 12.6667C32.0121 12.9206 33.523 13.6032 34.7506 14.7143C36.0097 15.7936 36.6392 17.2381 36.6392 19.0476C36.6392 20.9524 35.9153 22.5873 34.4673 23.9524C33.0508 25.3175 31.2252 26 28.9903 26ZM8.97094 26C7.17676 26 5.60291 25.5238 4.24939 24.5714C2.89588 23.619 1.8414 22.3651 1.08596 20.8095C0.361986 19.254 0 17.5556 0 15.7143C0 12.6032 1.03874 9.63492 3.11622 6.80952C5.1937 3.98413 7.90073 1.71429 11.2373 0L18.9806 4.38095C16.9031 5.36508 15.0775 6.53968 13.5036 7.90476C11.9298 9.26984 10.8281 10.8571 10.1985 12.6667C11.9927 12.9206 13.5036 13.6032 14.7312 14.7143C15.9903 15.7936 16.6199 17.2381 16.6199 19.0476C16.6199 20.9524 15.8959 22.5873 14.4479 23.9524C13.0315 25.3175 11.2058 26 8.97094 26Z"
                                        fill={theme.palette.warning['400']}
                                    />
                                </svg>
                                <Typography
                                    ref={quoteElement}
                                    variant={TypographyVariants.H6}
                                    className={classes.quote}
                                >
                                    {quote}
                                </Typography>
                                <svg
                                    className={classes.rightQuotationMarks}
                                    width="39"
                                    height="26"
                                    viewBox="0 0 39 26"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10.0097 4.44176e-06C11.8039 4.28491e-06 13.3777 0.476193 14.7312 1.42857C16.0847 2.38095 17.1392 3.63492 17.8947 5.19048C18.6186 6.74603 18.9806 8.44445 18.9806 10.2857C18.9806 13.3968 17.9419 16.3651 15.8644 19.1905C13.7869 22.0159 11.0799 24.2857 7.74334 26L-3.82995e-07 21.6191C2.07748 20.6349 3.90315 19.4603 5.477 18.0952C7.05085 16.7302 8.15254 15.1429 8.78208 13.3333C6.98789 13.0794 5.477 12.3968 4.2494 11.2857C2.99031 10.2064 2.36078 8.76191 2.36078 6.95238C2.36078 5.04762 3.08475 3.4127 4.53269 2.04762C5.94915 0.682542 7.77482 4.63714e-06 10.0097 4.44176e-06ZM30.0291 2.69161e-06C31.8232 2.53476e-06 33.3971 0.476191 34.7506 1.42857C36.1041 2.38095 37.1586 3.63492 37.914 5.19047C38.638 6.74603 39 8.44445 39 10.2857C39 13.3968 37.9613 16.3651 35.8838 19.1905C33.8063 22.0159 31.0993 24.2857 27.7627 26L20.0194 21.619C22.0969 20.6349 23.9225 19.4603 25.4964 18.0952C27.0702 16.7302 28.1719 15.1429 28.8015 13.3333C27.0073 13.0794 25.4964 12.3968 24.2688 11.2857C23.0097 10.2064 22.3801 8.76191 22.3801 6.95238C22.3801 5.04762 23.1041 3.4127 24.5521 2.04762C25.9685 0.68254 27.7942 2.88699e-06 30.0291 2.69161e-06Z"
                                        fill={theme.palette.warning['400']}
                                    />
                                </svg>
                                <Box className={classes.nameSection}>
                                    <Typography
                                        variant={TypographyVariants.H5}
                                        className={classes.name}
                                    >
                                        {name}
                                    </Typography>
                                    <Typography
                                        variant={TypographyVariants.H6}
                                        className={classes.certification}
                                    >
                                        {certification}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        style={{
                            height: '1000px' 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default Testimonial;
